import {
  IconAttention2,
  IconLock,
  IconMemo,
  IconTimeline,
} from "@studyplus/boron-ui";
import Time from "../../components/atoms/Time";
import { TimelineDrillProgressBar } from "../../components/features/Timeline/TimelineItem/DrillProgressBar";
import { TimelineCommentButton } from "../../components/features/Timeline/TimelineItem/TimelineItemFooter/TimelineCommentButton";
import { TimelineLikeButton } from "../../components/features/Timeline/TimelineItem/TimelineItemFooter/TimelineLikeButton";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../components/general/Popover/Popover";
import { Progress } from "../../domains/DrillProgress";
import OperatorInterface from "../../interfaces/OperatorInterface";
import StudentInterface from "../../interfaces/StudentInterface";
import type { StudyRecordWithRestrictedAttributes } from "../../interfaces/StudyRecordInterface";

type Props = {
  currentOperator: OperatorInterface;
  studyRecord: StudyRecordWithRestrictedAttributes;
  student: StudentInterface;
  sectionId: string;
  canSendComment: boolean;
  updateStudyRecordQueryCache: (
    newStudyRecord: StudyRecordWithRestrictedAttributes,
  ) => void;
  drillProgress?: Progress;
};

export const StudyRecordItem = (props: Props) => {
  return (
    <div className="relative m-2 h-auto min-w-[14rem] max-w-[14rem] rounded-sm border border-solid border-gray-400">
      <CommentIcon hasComment={!!props.studyRecord.comment} />
      <Body {...props} />
      <Footer {...props} />
    </div>
  );
};

const CommentIcon = ({ hasComment }: { hasComment: boolean }) => {
  // TODO: このアイコンは仮なので変更予定
  if (hasComment) {
    return (
      <IconAttention2 className="absolute right-0 top-0 p-1 text-blue-400 group-hover:opacity-50" />
    );
  } else {
    return (
      <IconAttention2 className="absolute right-0 top-0 p-1 text-gray-400 group-hover:opacity-50" />
    );
  }
};

const Body = (props: Props) => {
  const { studyRecord, drillProgress } = props;
  // Notee 教材はドリル基盤に載るため isTypeDrill は true になるが、通常教材として
  // 表示する（定着度バーを出さない）ため notee フラグで除外する。
  const isDrillMaterial =
    studyRecord.learningMaterial?.isTypeDrill &&
    !studyRecord.learningMaterial?.notee;

  return (
    <div className="group-hover:opacity-50">
      <LearningMaterialImage learningMaterial={studyRecord.learningMaterial} />
      <LearningMaterialName learningMaterial={studyRecord.learningMaterial} />

      <StudyRecordContent studyRecord={studyRecord} />

      {/* ドリル教材用プログレスバー */}
      <div className="mx-2 my-3">
        <TimelineDrillProgressBar
          progress={drillProgress}
          rounded
          invisible={!isDrillMaterial || !drillProgress}
        />
      </div>
    </div>
  );
};

const LearningMaterialImage = ({
  learningMaterial,
}: {
  learningMaterial: StudyRecordWithRestrictedAttributes["learningMaterial"];
}) => {
  return (
    <div className="flex h-[12rem] min-w-[10rem] items-center justify-center pt-7 text-center">
      {learningMaterial && learningMaterial.imageUrl && (
        <img
          className="max-h-full w-[8rem] object-contain"
          src={learningMaterial.imageUrl}
        />
      )}
    </div>
  );
};

const StudyRecordContent = ({ studyRecord }: Pick<Props, "studyRecord">) => {
  const isRestrictions =
    studyRecord.restrictedAttributes.includes("numberOfSeconds") &&
    studyRecord.restrictedAttributes.includes("amount");

  if (isRestrictions) {
    return <RestrictedStudyRecordTime />;
  } else {
    return <StudyRecord studyRecord={studyRecord} />;
  }
};

const RestrictedStudyRecordTime = () => {
  return (
    <PopoverProvider>
      <Popover>
        <PopoverTrigger className="flex cursor-pointer">
          <div className="mt-4 flex justify-center">
            <IconLock className="text-gray-800" />
            <div>
              <Time seconds={0} size="small" isRestrictions />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent>
            <div className="text-center">
              学習管理サービスを追加すると生徒が記録した
              <br />
              全学習記録が閲覧できます
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </PopoverProvider>
  );
};

const StudyRecord = ({ studyRecord }: Pick<Props, "studyRecord">) => {
  return (
    <div className="mt-4 flex w-full justify-center">
      {studyRecord.numberOfSeconds && studyRecord.numberOfSeconds > 0 ? (
        <>
          <IconTimeline className="text-gray-800" />
          <div>
            <Time seconds={studyRecord.numberOfSeconds} size="small" />
          </div>
        </>
      ) : studyRecord.amount && studyRecord.amount > 0 ? (
        <>
          <IconMemo className="min-w-fit text-gray-800" />
          <Amount unit={studyRecord.unit} amount={studyRecord.amount} />
        </>
      ) : (
        // NOTE: 要素の高さを合わせるためにinvisibleでスペースを確保
        <>
          <div className="invisible">
            <Amount unit={""} amount={0} />
          </div>
        </>
      )}
    </div>
  );
};

type AmountProps = {
  amount: number;
  unit: string;
};
const Amount = (props: AmountProps) => {
  return (
    <div className="overflow-hidden text-ellipsis whitespace-nowrap">
      <span className="mx-1 text-3xl">{props.amount}</span>
      <span>{props.unit}</span>
    </div>
  );
};

const LearningMaterialName = ({
  learningMaterial,
}: {
  learningMaterial: StudyRecordWithRestrictedAttributes["learningMaterial"];
}) => {
  return (
    <div className="mb-3 line-clamp-2 h-7 px-6 pt-2 break-words">
      {learningMaterial && learningMaterial.name
        ? learningMaterial.name
        : "教材なし"}
    </div>
  );
};

const Footer = (props: Props) => {
  const {
    currentOperator,
    studyRecord,
    student,
    canSendComment,
    updateStudyRecordQueryCache,
  } = props;
  return (
    <div className="mb-2 mt-2 flex w-full justify-evenly hover:opacity-100">
      <TimelineLikeButton
        currentOperator={currentOperator}
        studyRecord={studyRecord}
        student={student}
        canSendComment={canSendComment}
        noLabel={true}
        updateStudyRecordQueryCache={updateStudyRecordQueryCache}
      />

      <TimelineCommentButton
        restrictedAttributes={studyRecord.restrictedAttributes}
        totalComments={studyRecord.comments.length}
        toggleVisibility={() => {}}
        noLabel={true}
      />
    </div>
  );
};
